import axiosInstance from "./axiosInstanceV1";

/**
 * Stripe API functions for BodhiStreams
 */

// ========== Subscription Management ==========

/**
 * Create Stripe checkout session for subscription
 * @param {string} membershipId - Membership ID
 * @param {boolean} isYearly - Whether to use yearly billing
 * @returns {Promise<Object>} Checkout session data
 */
export const createCheckoutSession = async (membershipId, isYearly = false) => {
	const response = await axiosInstance.post(
		"/user/stripe/create-checkout-session",
		{
			membership_id: membershipId,
			is_yearly: isYearly,
		},
	);
	return response.data;
};

/**
 * Verify subscription after successful payment
 * @param {string} sessionId - Checkout session ID
 * @returns {Promise<Object>} Verification result
 */
export const verifySubscription = async (sessionId) => {
	const response = await axiosInstance.post(
		"/user/stripe/verify-subscription",
		{
			session_id: sessionId,
		},
	);
	return response.data;
};

/**
 * Create customer portal session for subscription management
 * @returns {Promise<Object>} Portal session data
 */
export const createCustomerPortalSession = async () => {
	const response = await axiosInstance.post("/user/stripe/customer-portal", {});
	return response.data;
};

/**
 * Get current user's subscription details
 * @returns {Promise<Object>} User subscription data
 */
export const getUserSubscription = async () => {
	try {
		const response = await axiosInstance.get("/user/stripe/subscription");
		return response.data;
	} catch (error) {
		if (error?.response?.status === 404) {
			return null;
		}

		throw error;
	}
};

// ========== Admin: Products ==========

/**
 * List all Stripe products
 * @param {Object} params - Query parameters (limit, starting_after, etc.)
 * @returns {Promise<Object>} Products list
 */
export const listProducts = async (params = {}) => {
	const queryParams = new URLSearchParams(params).toString();
	const response = await axiosInstance.get(
		`/admin/stripe/products${queryParams ? `?${queryParams}` : ""}`,
	);
	return response.data;
};

/**
 * Create a new Stripe product
 * @param {Object} productData - Product data
 * @returns {Promise<Object>} Created product
 */
export const createProduct = async (productData) => {
	const response = await axiosInstance.post(
		"/admin/stripe/products",
		productData,
	);
	return response.data;
};

/**
 * Update a Stripe product
 * @param {string} productId - Product ID
 * @param {Object} productData - Product data to update
 * @returns {Promise<Object>} Updated product
 */
export const updateProduct = async (productId, productData) => {
	const response = await axiosInstance.put(
		`/admin/stripe/products/${productId}/update`,
		productData,
	);
	return response.data;
};

/**
 * Delete a Stripe product
 * @param {string} productId - Product ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteProduct = async (productId) => {
	const response = await axiosInstance.delete(
		`/admin/stripe/products/${productId}`,
	);
	return response.data;
};

// ========== Admin: Prices ==========

/**
 * List all Stripe prices
 * @param {Object} params - Query parameters (limit, starting_after, etc.)
 * @returns {Promise<Object>} Prices list
 */
export const listPrices = async (params = {}) => {
	const queryParams = new URLSearchParams(params).toString();
	const response = await axiosInstance.get(
		`/admin/stripe/prices${queryParams ? `?${queryParams}` : ""}`,
	);
	return response.data;
};

/**
 * Create a new Stripe price
 * @param {Object} priceData - Price data
 * @returns {Promise<Object>} Created price
 */
export const createPrice = async (priceData) => {
	const response = await axiosInstance.post("/admin/stripe/prices", priceData);
	return response.data;
};

/**
 * Update a Stripe price
 * @param {string} priceId - Price ID
 * @param {Object} priceData - Price data to update
 * @returns {Promise<Object>} Updated price
 */
export const updatePrice = async (priceId, priceData) => {
	const response = await axiosInstance.put(
		`/admin/stripe/prices/${priceId}/update`,
		priceData,
	);
	return response.data;
};

/**
 * Delete a Stripe price
 * @param {string} priceId - Price ID
 * @returns {Promise<Object>} Deletion result
 */
export const deletePrice = async (priceId) => {
	const response = await axiosInstance.delete(
		`/admin/stripe/prices/${priceId}`,
	);
	return response.data;
};

// ========== Admin: Subscriptions ==========

/**
 * List all Stripe subscriptions
 * @param {Object} params - Query parameters (limit, starting_after, etc.)
 * @returns {Promise<Object>} Subscriptions list
 */
export const listSubscriptions = async (params = {}) => {
	const queryParams = new URLSearchParams(params).toString();
	const response = await axiosInstance.get(
		`/admin/stripe/subscriptions${queryParams ? `?${queryParams}` : ""}`,
	);
	return response.data;
};

/**
 * List stored Stripe webhook and action logs
 * @param {Object} params - Query parameters (limit, event_type, log_type, status, user_id)
 * @returns {Promise<Object>} Stored Stripe logs
 */
export const listStripeLogs = async (params = {}) => {
	const queryParams = new URLSearchParams(params).toString();
	const response = await axiosInstance.get(
		`/admin/stripe/logs${queryParams ? `?${queryParams}` : ""}`,
	);
	return response.data;
};

/**
 * Get a specific Stripe subscription
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<Object>} Subscription data
 */
export const getSubscription = async (subscriptionId) => {
	const response = await axiosInstance.get(
		`/admin/stripe/subscriptions/${subscriptionId}`,
	);
	return response.data;
};

/**
 * Cancel a Stripe subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {boolean} atPeriodEnd - Cancel at period end or immediately
 * @returns {Promise<Object>} Cancelled subscription
 */
export const cancelSubscription = async (
	subscriptionId,
	atPeriodEnd = true,
) => {
	const response = await axiosInstance.post(
		`/admin/stripe/subscriptions/${subscriptionId}/cancel`,
		{
			at_period_end: atPeriodEnd,
		},
	);
	return response.data;
};

/**
 * Reactivate a Stripe subscription
 * @param {string} subscriptionId - Subscription ID
 * @returns {Promise<Object>} Reactivated subscription
 */
export const reactivateSubscription = async (subscriptionId) => {
	const response = await axiosInstance.post(
		`/admin/stripe/subscriptions/${subscriptionId}/reactivate`,
	);
	return response.data;
};

// ========== Admin: Customers ==========

/**
 * List all Stripe customers
 * @param {Object} params - Query parameters (limit, starting_after, etc.)
 * @returns {Promise<Object>} Customers list
 */
export const listCustomers = async (params = {}) => {
	const queryParams = new URLSearchParams(params).toString();
	const response = await axiosInstance.get(
		`/admin/stripe/customers${queryParams ? `?${queryParams}` : ""}`,
	);
	return response.data;
};

/**
 * Create a new Stripe customer
 * @param {Object} customerData - Customer data
 * @returns {Promise<Object>} Created customer
 */
export const createCustomer = async (customerData) => {
	const response = await axiosInstance.post(
		"/admin/stripe/customers",
		customerData,
	);
	return response.data;
};

/**
 * Get a specific Stripe customer
 * @param {string} customerId - Customer ID
 * @returns {Promise<Object>} Customer data
 */
export const getCustomer = async (customerId) => {
	const response = await axiosInstance.get(
		`/admin/stripe/customers/${customerId}`,
	);
	return response.data;
};

/**
 * Update a Stripe customer
 * @param {string} customerId - Customer ID
 * @param {Object} customerData - Customer data to update
 * @returns {Promise<Object>} Updated customer
 */
export const updateCustomer = async (customerId, customerData) => {
	const response = await axiosInstance.put(
		`/admin/stripe/customers/${customerId}/update`,
		customerData,
	);
	return response.data;
};

/**
 * Delete a Stripe customer
 * @param {string} customerId - Customer ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteCustomer = async (customerId) => {
	const response = await axiosInstance.delete(
		`/admin/stripe/customers/${customerId}`,
	);
	return response.data;
};
